/**
 * TENANT-5504: recognising a session the API no longer honours.
 *
 * A stored JWT can outlive the account behind it (the profile was removed from the API DB).
 * The API answers such a session with HTTP 401 on every authenticated route. The body carries
 * an `error_code`, but its name is not part of a stable contract yet, so the STATUS is the
 * only signal read here.
 */

export const HTTP_UNAUTHORIZED = 401

/**
 * What `SessionService.handleRejectedSession` did with a rejected token.
 * - `cleared` — it was the stored session; now signed out.
 * - `already_signed_out` — no usable session is stored (a concurrent 401 or a logout got there first).
 * - `newer_session_kept` — a different session was signed in since the request was sent; kept.
 */
export const REJECTED_SESSION_OUTCOME = Object.freeze({
    ALREADY_SIGNED_OUT: 'already_signed_out',
    CLEARED: 'cleared',
    NEWER_SESSION_KEPT: 'newer_session_kept',
})

/**
 * Endpoints whose 401 is about the credentials being exchanged or revoked, not about the stored
 * session: logging in, logging out, and the OAuth hand-off. Treating their 401 as "the stored
 * session was rejected" would clear the session from inside the flows that establish or end it,
 * and `SessionService.delete()` calls `DELETE /session` — reacting to it would loop.
 */
const SESSION_CREDENTIAL_ENDPOINT_PREFIXES = Object.freeze([
    '/session',
    '/core-auth/',
])

/**
 * A token is "usable" only when it's a non-empty string AND isn't one of the
 * sentinel values `'null'` / `'undefined'` that can leak out of localStorage
 * via prior buggy writes. Without this guard, callers would send
 * `Authorization: Bearer null`. Any other non-empty value is sent, so a
 * garbage stored token reaches the API and comes back as a 401
 * (`session_token_malformed`), which signs the session out.
 */
export const hasUsableToken = (token) => {
    if (!token) return false
    const trimmedToken = String(token).trim()
    return trimmedToken !== '' && trimmedToken !== 'null' && trimmedToken !== 'undefined'
}

/**
 * @param {string} endpoint - API path as passed to `BaseAPI.fetchSecure` (may carry a query string).
 * @returns {boolean} true when the endpoint issues or revokes credentials.
 */
export const isSessionCredentialEndpoint = (endpoint) => {
    if (typeof endpoint !== 'string') {
        throw new TypeError('isSessionCredentialEndpoint requires the endpoint path.')
    }
    const [path] = endpoint.split('?')
    return SESSION_CREDENTIAL_ENDPOINT_PREFIXES.some((prefix) => {
        if (prefix.endsWith('/')) return path.startsWith(prefix)
        return path === prefix || path.startsWith(`${prefix}/`)
    })
}

/**
 * Whether a response means the session that was SENT has been rejected.
 *
 * Only a request that carried a token can have its session rejected: an anonymous request to a
 * protected endpoint is answered 401 too, and reacting to it would re-prompt on every public call.
 *
 * @param {{ endpoint: string, status: number, sentToken: unknown }} request
 * @returns {boolean}
 */
export const isRejectedSessionResponse = ({
    endpoint,
    sentToken,
    status,
}) => status === HTTP_UNAUTHORIZED
    && hasUsableToken(sentToken)
    && !isSessionCredentialEndpoint(endpoint)

/**
 * Whether an error thrown through `fetchResponseHandler` is a rejected session.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isRejectedSessionError = (error) => typeof error === 'object'
    && error !== null
    && 'statusCode' in error
    && error.statusCode === HTTP_UNAUTHORIZED
